import {
  createContext,
  useContext,
  useState,
  type MouseEvent,
  type ReactNode,
} from 'react'
import {
  Box,
  InputBase,
  Popover,
  Stack,
  Typography,
  type SxProps,
  type Theme,
} from '@mui/material'
import CheckIcon from '@mui/icons-material/Check'

import { colorTokens } from '@/apps/latest/shared/styles/colorTokens'
import { LAYOUT } from '@/shared/styles/spacing'
import { RADIUS } from '@/shared/styles/radii'
import {
  FONT_WEIGHT,
  scaledFontSize,
  typeScale,
} from '@/shared/styles/typography'

import { useNutritionBrand } from './brand'
import { formatYmd } from './format'
import { LABEL_BY_KEY } from './reportFields'
import { useSaveNutritionReportField } from './useNutritionReport'
import type { NutritionReportFields, NutritionStamp } from './api'

interface Props {
  eventId: string
  fields: NutritionReportFields
  // コードが書く値 (実施日・指導時間・署名)。出力と同じ値を同じ位置に見せるだけ。
  stamp?: NutritionStamp
  // 右の列の上の段 (メモ)。持ち主は event (memo_items) なので中身は呼び出し側が渡す。
  memo?: ReactNode
}

// --- 様式の格子 ------------------------------------------------------------------
// 報告書 Excel (医療機関用シート。2026-10-24 受領の原本 = 間食は就床の後・A と P は 5 行) の左半分を、
// セルの格子ごと写す。列幅は様式の A〜O 列 (Excel の文字数単位) を fr にし、 セルは様式の番地 (例 C6:K6) で置く。罫線は 1px の
// hairline だけで、 見出しセルの塗り・角丸は持たない (design-book 04-apps/kakeru.md
// 「管理栄養士の報告書」。白い紙の「全セルに罫線」からの意図的逸脱)。
// 列ごとに最小の幅 (px) を持つ。様式の比 (紙の 10pt) を画面の 13〜14px の字にそのまま写すと、狭い列 (A・M・N) の見出しが
// 縦に折れ (「備考」「先生御侍史」)、N の指導回数の値が 5px になって字が切れた (design-book ADR-0081)。最小は列に置く
// 見出しの字数から決める (A: 備考・B: 短期目標・L:M: 指導を受けた人・M:O: 体型判定 + 値)。合計 744px。
const COLUMN_MIN_PX: Record<string, number> = {
  A: 44,
  B: 72,
  C: 48,
  D: 48,
  E: 48,
  F: 48,
  G: 48,
  H: 48,
  I: 48,
  J: 48,
  K: 48,
  L: 56,
  M: 52,
  N: 40,
  O: 48,
}
const COLUMN_WIDTHS: Record<string, number> = {
  A: 5.38,
  B: 13,
  C: 13,
  D: 13,
  E: 13,
  F: 13,
  G: 13,
  H: 13,
  I: 13,
  J: 13,
  K: 13,
  L: 13,
  M: 7.88,
  N: 5.38,
  O: 13,
}
const COLUMNS = Object.keys(COLUMN_WIDTHS)
const GRID_TEMPLATE_COLUMNS =
  COLUMNS.map(
    (c) => `[${c}] minmax(${COLUMN_MIN_PX[c]}px, ${COLUMN_WIDTHS[c]}fr)`
  ).join(' ') + ' [END]'
// 格子の最小の幅 (列の最小の和)。右の列はこれより狭くなるときに格子の下へ回る
const GRID_MIN_WIDTH = Object.values(COLUMN_MIN_PX).reduce((a, b) => a + b, 0)
// 結合セルの終端 (例 K) の次の列線 (L)。O の次は END。
const lineAfter = (column: string): string =>
  COLUMNS[COLUMNS.indexOf(column) + 1] ?? 'END'

const CELL_REF = /^([A-O])(\d+)(?::([A-O])(\d+))?$/

// ブロック = 様式で罫線に囲まれた行のまとまり (6〜8 行目など)。from は先頭の行番号。
// 罫線は gap 1px の下地色で引く (セル同士の境界にだけ線が出て、 太さが揃う)。
// plain は 3〜4 行目の頭 (様式に罫線が無い)。
const BlockBaseRow = createContext(1)

const Block = ({
  from,
  plain,
  children,
}: {
  from: number
  plain?: boolean
  children: ReactNode
}) => (
  <BlockBaseRow.Provider value={from}>
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: GRID_TEMPLATE_COLUMNS,
        gridAutoRows: `minmax(${LAYOUT.blockRowMinHeight}px, auto)`,
        ...(plain
          ? {}
          : {
              gap: '1px',
              backgroundColor: colorTokens.border.subtle,
              border: `1px solid ${colorTokens.border.default}`,
            }),
      }}
    >
      {children}
    </Box>
  </BlockBaseRow.Provider>
)

// セル。at は様式の番地 (A6 / C6:K6)。label は見出しセル (fg.muted・中央)、
// それ以外は値のセルで、 中の入力がセルいっぱいに広がる。
const Cell = ({
  at,
  label,
  align = 'center',
  children,
}: {
  at: string
  label?: boolean
  align?: 'left' | 'center' | 'right'
  children?: ReactNode
}) => {
  const base = useContext(BlockBaseRow)
  const m = CELL_REF.exec(at)
  if (!m) throw new Error(`様式の番地ではありません: ${at}`)
  const [, c1, r1, c2 = c1, r2 = r1] = m
  const justify = { left: 'flex-start', center: 'center', right: 'flex-end' }[
    align
  ]
  return (
    <Box
      sx={{
        gridColumn: `${c1} / ${lineAfter(c2)}`,
        gridRow: `${Number(r1) - base + 1} / ${Number(r2) - base + 2}`,
        minWidth: 0,
        display: 'flex',
        alignItems: label ? 'center' : 'stretch',
        justifyContent: label ? justify : 'flex-start',
        // 罫線を gap の下地で引くので地色が要る。ブロック (Paper) と同じ色にして箱に見せない
        bgcolor: 'background.paper',
        ...(label && {
          px: 1,
          py: 0.5,
          color: colorTokens.fg.muted,
          fontSize: scaledFontSize('label'),
          lineHeight: typeScale.label.lineHeight,
          textAlign: align,
          // 見出しは 1 行 (列の最小の幅で収まる。縦に折れると読めない)
          whiteSpace: 'nowrap',
        }),
      }}
    >
      {children}
    </Box>
  )
}

// 値のセルの中の印字 (単位・「エネルギー：」など)。
const Inline = ({ children }: { children: ReactNode }) => (
  <Typography
    variant="caption"
    component="span"
    sx={{
      alignSelf: 'center',
      px: 0.5,
      color: colorTokens.fg.muted,
      fontSize: scaledFontSize('label'),
      lineHeight: typeScale.label.lineHeight,
      whiteSpace: 'nowrap',
    }}
  >
    {children}
  </Typography>
)

// 数式で決まる値 (BMI など)。編集できないので入力の見た目を持たない。
const Static = ({ children }: { children?: ReactNode }) => (
  <Typography
    variant="body2"
    component="span"
    sx={{
      alignSelf: 'center',
      minWidth: 48,
      px: 1,
      color: colorTokens.fg.default,
      fontSize: scaledFontSize('bodySm'),
      whiteSpace: 'nowrap',
    }}
  >
    {children}
  </Typography>
)

// 右の列の枠。見出し (格子の見出しセルと同じ字) + 中身の 2 段。
const Frame = ({
  label,
  sx,
  children,
}: {
  label: string
  sx?: SxProps<Theme>
  children: ReactNode
}) => (
  <Box
    sx={[
      {
        display: 'flex',
        flexDirection: 'column',
        border: `1px solid ${colorTokens.border.default}`,
      },
      ...(Array.isArray(sx) ? sx : [sx]),
    ]}
  >
    <Box
      sx={{
        flexShrink: 0,
        px: 1,
        py: 0.5,
        borderBottom: `1px solid ${colorTokens.border.subtle}`,
        color: colorTokens.fg.muted,
        fontSize: scaledFontSize('label'),
        lineHeight: typeScale.label.lineHeight,
      }}
    >
      {label}
    </Box>
    {children}
  </Box>
)

// 値の入力。セルいっぱいに広がる。格子の中では hover / focus だけの affordance (text-field
// §2b) だと印字の欄と見分けがつかないので、 入力できる欄は常時白 (bg.default) にし、 見出し・
// 数式の欄は block の地色のまま。focus は humble なリング。
// フォーカスが外れたらその欄だけ保存する。作り直し・別端末の保存でサーバ値が変わったら追従し、
// 編集中の欄は上書きしない (= "Adjusting state when props change")。
const Field = ({
  fieldKey,
  value,
  onSave,
  minRows = 1,
  width,
  block,
}: {
  fieldKey: string
  value: string
  onSave: (key: string, value: string) => void
  // 段落の欄 (S・A・備考など) はセルが跨ぐ行数ぶんの高さを持つ。
  minRows?: number
  // 一語の欄 (数値) の幅。省略時はセルの残りいっぱい。
  width?: number
  // flex のセルでなく block の枠に置く (右半分)。幅は枠いっぱい、 高さは本文なり。
  block?: boolean
}) => {
  const [text, setText] = useState(value)
  const [focused, setFocused] = useState(false)
  const [seenValue, setSeenValue] = useState(value)
  if (value !== seenValue) {
    setSeenValue(value)
    if (!focused) setText(value)
  }

  const commit = () => {
    setFocused(false)
    if (text === value) return
    onSave(fieldKey, text)
  }

  return (
    <InputBase
      id={`nutrition-report-${fieldKey}`}
      value={text}
      onChange={(e) => setText(e.target.value)}
      onFocus={() => setFocused(true)}
      onBlur={commit}
      multiline
      minRows={minRows}
      placeholder="-"
      inputProps={{ 'aria-label': LABEL_BY_KEY[fieldKey] }}
      sx={{
        flex: width ? 'none' : 1,
        width: block ? '100%' : width,
        minWidth: 0,
        alignSelf: 'stretch',
        alignItems: 'flex-start',
        px: 1,
        py: 0.5,
        fontSize: scaledFontSize('bodySm'),
        lineHeight: typeScale.bodySm.lineHeight,
        color: colorTokens.fg.default,
        backgroundColor: colorTokens.bg.default,
        '&.Mui-focused': {
          boxShadow: colorTokens.focus.ringNeutral,
          position: 'relative',
          zIndex: 1,
        },
        // 空の欄は「-」1 文字 (44 行の格子に「未記入」が並ぶと字の方が目立つ)
        '& textarea::placeholder': {
          color: colorTokens.fg.faint,
          opacity: 1,
        },
      }}
    />
  )
}

// 体型判定 (design-book ADR-0079)。体組成計の結果の「体脂肪率と筋肉量による体型判定」の 3×3 の表と
// 同じ並び (上から体脂肪率が多い → 少ない、左から筋肉量が少ない → 多い) のマスから選ぶ。人は印の位置と
// 同じマスを押すだけ。語はタニタの結果用紙の語 (からだカルテの画面は 3 マスの語が違うが並びは同じ)。
export const BODY_TYPE_CHART: string[][] = [
  ['かくれ肥満', '肥満型', 'かた太り'],
  ['運動不足', '標準', '筋肉質'],
  ['やせ型', '細身筋肉質', '筋肉質'],
]

// 欄は他の入力と同じ白いセル (格子の中では白が「入力できる」)。表に無い語 (前に打たれた値) はそのまま見せ、
// マスを押すまで変えない。
const BodyTypeField = ({
  value,
  onSave,
}: {
  value: string
  onSave: (key: string, value: string) => void
}) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null)
  const pick = (next: string) => {
    setAnchor(null)
    if (next !== value) onSave('body_type', next)
  }

  return (
    <>
      <Box
        component="button"
        type="button"
        aria-haspopup="dialog"
        aria-label={LABEL_BY_KEY.body_type}
        onClick={(e: MouseEvent<HTMLButtonElement>) =>
          setAnchor(e.currentTarget)
        }
        sx={{
          flex: 1,
          minWidth: 0,
          alignSelf: 'stretch',
          display: 'flex',
          alignItems: 'center',
          px: 1,
          py: 0.5,
          border: 'none',
          fontFamily: 'inherit',
          textAlign: 'left',
          fontSize: scaledFontSize('bodySm'),
          lineHeight: typeScale.bodySm.lineHeight,
          color: value ? colorTokens.fg.default : colorTokens.fg.faint,
          backgroundColor: colorTokens.bg.default,
          cursor: 'pointer',
          '&:focus-visible': {
            outline: 'none',
            boxShadow: colorTokens.focus.ringNeutral,
            position: 'relative',
            zIndex: 1,
          },
          ...(anchor && {
            boxShadow: colorTokens.focus.ringNeutral,
            position: 'relative',
            zIndex: 1,
          }),
        }}
      >
        {value || '-'}
      </Box>
      <Popover
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        slotProps={{
          paper: {
            elevation: 0,
            sx: {
              mt: 0.5,
              p: 0.5,
              borderRadius: `${RADIUS.lg}px`,
              border: `1px solid ${colorTokens.border.subtle}`,
              boxShadow: colorTokens.shadow.popover,
            },
          },
        }}
      >
        <Box
          role="group"
          aria-label={LABEL_BY_KEY.body_type}
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, minmax(88px, auto))',
            gap: 0.5,
          }}
        >
          {BODY_TYPE_CHART.flatMap((row, r) =>
            row.map((word, c) => {
              const selected = word === value
              return (
                <Box
                  key={`${r}-${c}`}
                  component="button"
                  type="button"
                  aria-pressed={selected}
                  onClick={() => pick(word)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    minHeight: 32,
                    px: 1,
                    border: 'none',
                    borderRadius: `${RADIUS.md}px`,
                    fontFamily: 'inherit',
                    fontSize: 14,
                    fontWeight: selected
                      ? FONT_WEIGHT.semibold
                      : FONT_WEIGHT.regular,
                    color: colorTokens.fg.default,
                    bgcolor: 'transparent',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                    '&:hover, &:focus-visible': {
                      outline: 'none',
                      bgcolor: colorTokens.overlay.hover,
                    },
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      width: 16,
                      flexShrink: 0,
                      display: 'inline-flex',
                    }}
                  >
                    {selected && <CheckIcon sx={{ fontSize: 16 }} />}
                  </Box>
                  {word}
                </Box>
              )
            })
          )}
        </Box>
        {value && (
          <>
            <Box
              sx={{
                height: '1px',
                my: 0.5,
                bgcolor: colorTokens.border.default,
              }}
            />
            <Box
              component="button"
              type="button"
              onClick={() => pick('')}
              sx={{
                display: 'flex',
                alignItems: 'center',
                width: '100%',
                minHeight: 32,
                px: 1,
                pl: 3.5,
                border: 'none',
                borderRadius: `${RADIUS.md}px`,
                fontFamily: 'inherit',
                fontSize: 14,
                color: colorTokens.fg.muted,
                bgcolor: 'transparent',
                cursor: 'pointer',
                '&:hover, &:focus-visible': {
                  outline: 'none',
                  bgcolor: colorTokens.overlay.hover,
                },
              }}
            >
              空にする
            </Box>
          </>
        )}
      </Popover>
    </>
  )
}

// --- 数式で決まる欄 ------------------------------------------------------------
// 様式では BMI・IBW が数式 (I17・K17)。画面でも同じ値を見せるだけで、 保存はしない (書き手は Excel の数式)。
// 体型判定は数式でなく欄 (体組成計の表から人が選ぶ。design-book ADR-0077・0079)。
const firstNumber = (s: string): number | null => {
  const m = s
    .replace(/[０-９．]/g, (c) => String.fromCharCode(c.charCodeAt(0) - 0xfee0))
    .match(/\d+(\.\d+)?/)
  return m ? Number(m[0]) : null
}

export const deriveBmiIbw = (
  height: string,
  weight: string
): { bmi: string; ibw: string } => {
  const h = firstNumber(height)
  const w = firstNumber(weight)
  if (!h || h <= 0) return { bmi: '', ibw: '' }
  const hm = h / 100
  const ibw = (hm * hm * 22).toFixed(1)
  if (!w || w <= 0) return { bmi: '', ibw }
  return { bmi: (w / (hm * hm)).toFixed(1), ibw }
}

// 様式の「( 歳)」。生年月日 (西暦の年月日が読めるとき) と実施日から満年齢。
export const deriveAge = (birthday: string, at?: string): string => {
  const m = birthday
    .replace(/[０-９]/g, (c) => String.fromCharCode(c.charCodeAt(0) - 0xfee0))
    .match(/(\d{4})\D+(\d{1,2})\D+(\d{1,2})/)
  const today = at ? new Date(at) : new Date()
  if (!m || Number.isNaN(today.getTime())) return ''
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])]
  let age = today.getFullYear() - y
  const beforeBirthday =
    today.getMonth() + 1 < mo ||
    (today.getMonth() + 1 === mo && today.getDate() < d)
  if (beforeBirthday) age -= 1
  return age >= 0 && age < 130 ? String(age) : ''
}

// --- 報告書 ----------------------------------------------------------------------
// 様式 1 枚 = 左に格子・右の列に自由記載 (Q7:AE44 の 1 セル)。右の列は上にメモ、 下に要約
// (会話から LLM が書き、直せる) の 2 段で、 出力の右半分も同じ並びで [メモ] [要約] と見出しを
// 付けて書く (back の right_half)。栄養価・資料・会話は同じ記録の別のタブ (NutritionRoundTabs。design-book ADR-0082)。
// 右の列は、格子の最小の幅と並べて入らない幅では格子の下に回る (折り返す)。
// 番地は医療機関用シートのまま。薬局用・カフェ用にしか無い欄 (相談内容) は、 その様式で
// 置かれている位置 (指示栄養量の下・S の手前) に 1 行足す。
// コードが書く値 (実施日・指導時間・署名) は back の stamp を見せるだけ。患者 ID は出すの
// popover (NutritionExportButton) に置く (薬局用にしか無い欄)。
export const NutritionReportPanel = ({
  eventId,
  fields,
  stamp,
  memo,
}: Props) => {
  const save = useSaveNutritionReportField(eventId)
  const brand = useNutritionBrand()
  const handleSave = (key: string, value: string) => save.mutate({ key, value })
  const v = (key: string) => fields[key] ?? ''
  const field = (
    key: string,
    opts?: { minRows?: number; width?: number; block?: boolean }
  ) => <Field fieldKey={key} value={v(key)} onSave={handleSave} {...opts} />
  const metrics = deriveBmiIbw(v('height'), v('weight'))
  const age = deriveAge(v('birthday'), stamp?.recorded_on)

  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 3,
        alignItems: 'flex-start',
      }}
    >
      <Stack spacing={1} sx={{ flex: `7 1 ${GRID_MIN_WIDTH}px`, minWidth: 0 }}>
        {/* 3〜4 行目: 宛先と実施日 (罫線なし) */}
        <Block from={3} plain>
          <Cell at="A3:F3">{field('institution')}</Cell>
          <Cell at="G3:H3" label align="right">
            実施日
          </Cell>
          <Cell at="I3:K3">
            <Static>{stamp ? formatYmd(stamp.recorded_on) : ''}</Static>
          </Cell>
          <Cell at="L3:M3" label align="right">
            指導を受けた人
          </Cell>
          <Cell at="N3:O3">{field('counseled_person')}</Cell>
          {/* 様式では値と後ろの字 (先生 御侍史・分・回目) が別のセル。画面は 身長 [170] cm と同じく 1 つのセルに
              値と単位を並べる (狭い 1 列に値だけ置くと字が切れる) */}
          <Cell at="A4:F4">
            {field('physician')}
            <Inline>先生 御侍史</Inline>
          </Cell>
          <Cell at="G4:H4" label align="right">
            指導時間
          </Cell>
          <Cell at="I4:K4">
            <Static>{stamp?.minutes ?? ''}</Static>
            <Inline>分</Inline>
          </Cell>
          <Cell at="L4:M4" label align="right">
            指導回数
          </Cell>
          <Cell at="N4:O4">
            {field('session_number', { width: 48 })}
            <Inline>回目</Inline>
          </Cell>
        </Block>

        {/* 6〜8 行目: 患者 */}
        <Block from={6}>
          <Cell at="A6:B6" label>
            ふりがな
          </Cell>
          <Cell at="C6:K6">{field('name_kana')}</Cell>
          <Cell at="L6:M7" label>
            性別
          </Cell>
          <Cell at="N6:O7">{field('sex')}</Cell>
          <Cell at="A7:B7" label>
            患者氏名
          </Cell>
          <Cell at="C7:K7">{field('name')}</Cell>
          <Cell at="A8:B8" label>
            生年月日
          </Cell>
          <Cell at="C8:K8">{field('birthday')}</Cell>
          <Cell at="L8:O8" label align="left">
            （{age && <Static>{age}</Static>}歳）
          </Cell>
        </Block>

        {/* 10〜11 行目: 医療機関からの指示 (+ 薬局用・カフェ用の相談内容) */}
        <Block from={10}>
          <Cell at="A10:B10" label>
            指示内容
          </Cell>
          <Cell at="C10:O10">{field('instruction')}</Cell>
          <Cell at="A11:B11" label>
            指示栄養量
          </Cell>
          <Cell at="C11:G11">
            <Inline>エネルギー：</Inline>
            {field('instructed_energy')}
            <Inline>kcal</Inline>
          </Cell>
          <Cell at="H11:O11">
            <Inline>その他：</Inline>
            {field('instructed_nutrition_other')}
          </Cell>
          <Cell at="A12:B12" label>
            相談内容
          </Cell>
          <Cell at="C12:O12">{field('consultation')}</Cell>
        </Block>

        {/* 13〜41 行目: S / O / A / P */}
        <Block from={13}>
          <Cell at="A13:A16" label>
            S
          </Cell>
          <Cell at="B13:O16">{field('subjective', { minRows: 4 })}</Cell>

          <Cell at="A17:A28" label>
            O
          </Cell>
          <Cell at="B17:D17">
            <Inline>身長</Inline>
            {field('height')}
            <Inline>cm</Inline>
          </Cell>
          <Cell at="E17:G17">
            <Inline>体重</Inline>
            {field('weight')}
            <Inline>kg</Inline>
          </Cell>
          <Cell at="H17:I17">
            <Inline>BMI</Inline>
            <Static>{metrics.bmi}</Static>
          </Cell>
          <Cell at="J17:L17">
            <Inline>IBW</Inline>
            <Static>{metrics.ibw}</Static>
            <Inline>kg</Inline>
          </Cell>
          <Cell at="M17:O17">
            <Inline>体型判定</Inline>
            <BodyTypeField value={v('body_type')} onSave={handleSave} />
          </Cell>
          {/* 検査値・服薬状況の見出しは B:C の 2 列、 起床〜運動は B の 1 列 (様式のまま)。間食は就床の後 */}
          {(
            [
              [18, 'lab_values', '検査値', 'C'],
              [19, 'medication', '服薬状況', 'C'],
              [20, 'wake_up', '起床', 'B'],
              [21, 'breakfast', '朝食', 'B'],
              [22, 'lunch', '昼食', 'B'],
              [23, 'dinner', '夕食', 'B'],
              [24, 'bedtime', '就床', 'B'],
              [25, 'snack', '間食', 'B'],
              [26, 'beverages', '飲料', 'B'],
              [27, 'alcohol', '飲酒', 'B'],
              [28, 'exercise', '運動', 'B'],
            ] as const
          ).map(([row, key, label, labelEnd]) => [
            <Cell key={`${key}-label`} at={`B${row}:${labelEnd}${row}`} label>
              {label}
            </Cell>,
            <Cell key={key} at={`${lineAfter(labelEnd)}${row}:O${row}`}>
              {field(key)}
            </Cell>,
          ])}

          {/* A と P は様式の 5 行 (①〜⑤) を 1 つの欄で持つ。1 行 1 項目で改行 */}
          <Cell at="A29:A33" label>
            A
          </Cell>
          <Cell at="B29:O33">{field('assessment', { minRows: 5 })}</Cell>

          <Cell at="A34:A41" label>
            P
          </Cell>
          <Cell at="B34:B38" label>
            短期目標
          </Cell>
          <Cell at="C34:O38">{field('short_term_goals', { minRows: 5 })}</Cell>
          <Cell at="B39:C39" label>
            長期目標
          </Cell>
          <Cell at="D39:O39">{field('long_term_goal')}</Cell>
          <Cell at="B40:C40" label>
            目標栄養量
          </Cell>
          <Cell at="D40:O40">{field('target_nutrition')}</Cell>
          <Cell at="B41:C41" label>
            配布資料
          </Cell>
          <Cell at="D41:O41">{field('handouts')}</Cell>
        </Block>

        {/* 43〜45 行目: 依頼店舗と備考 */}
        <Block from={43}>
          <Cell at="A43:B43" label>
            依頼店舗
          </Cell>
          <Cell at="C43:I43">
            {brand?.referringChain && <Inline>{brand.referringChain}</Inline>}
            {field('referring_store')}
            <Inline>店</Inline>
          </Cell>
          <Cell at="J43:K43" label>
            薬剤師
          </Cell>
          <Cell at="L43:O43">{field('referring_pharmacist')}</Cell>
          <Cell at="A44:A45" label>
            備考
          </Cell>
          <Cell at="B44:O45">{field('notes', { minRows: 2 })}</Cell>
        </Block>

        {/* 47〜48 行目: 継続と署名 (署名はアカウントから決まる) */}
        <Block from={47}>
          <Cell at="A47:O47">
            <Inline>継続栄養指導</Inline>
            {field('continuation', { width: 72 })}
            <Inline>（次回実施予定日は、</Inline>
            {field('next_date')}
            <Inline>です。）</Inline>
          </Cell>
          <Cell at="A48:O48">
            {brand?.organization && <Inline>{brand.organization}</Inline>}
            <Static>{stamp?.store}</Static>
            <Inline>店</Inline>
            <Static>{stamp?.dietitian}</Static>
            <Inline>管理栄養士</Inline>
          </Cell>
        </Block>
      </Stack>

      {/* 右の列: メモと要約の 2 段 (見出し付きの同じ枠)。画面では列ごと追従させ、 要約が長ければ
          その枠の中で送る。送る箱の中は block (flex にすると欄の高さが箱で止まり、 はみ出した
          本文の下地が消える) */}
      <Stack
        spacing={1}
        sx={{
          flex: '3 1 280px',
          minWidth: 0,
          position: 'sticky',
          top: 16,
          maxHeight: 'calc(100vh - 32px)',
        }}
      >
        {memo && (
          <Frame label="メモ" sx={{ flexShrink: 0 }}>
            <Box sx={{ px: 1, py: 0.5, bgcolor: colorTokens.bg.default }}>
              {memo}
            </Box>
          </Frame>
        )}
        <Frame label="要約" sx={{ minHeight: 0 }}>
          <Box sx={{ minHeight: 0, overflowY: 'auto' }}>
            {field('summary', { minRows: 12, block: true })}
          </Box>
        </Frame>
      </Stack>
    </Box>
  )
}
